import { CopyOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Alert, Button, Divider, Input, InputNumber, Select, Space, Switch, Tag, Typography } from 'antd';
import { useMemo } from 'react';
import {
  CONDITION_OPERATOR_LABEL,
  UPLOAD_TYPES,
  inputPortsOfSchema,
  operatorNeedsValue,
  type ParamField,
  type WorkflowConditionCase,
  type WorkflowConditionOperator,
  type WorkflowGraph,
  type WorkflowNode,
} from '@gw/shared';
import { CASE_COLORS, NODE_TYPE_LABEL, PORT_COLOR } from './nodes';
import type { NodeTargetInfo } from './WorkflowCanvas';

interface Props {
  graph: WorkflowGraph;
  node: WorkflowNode;
  onChange: (patch: Partial<WorkflowNode>) => void;
  onDelete: () => void;
  /** Nhan ban node dang chon (ban sao lech vi tri, khong keo theo day). */
  onDuplicate: () => void;
  /**
   * "LAY THAM SO TU INPUT CONG KHAI": khai bao cac tham so cua node nay vao paramsSchema
   * cua workflow (gia tri co dinh hien tai -> default) + tro tham so node ve {{input.<ten>}}.
   * Ket qua: phan mem thu 3 goi workflow y het goi thang model/nhom nay.
   */
  onExposeParams: () => void;
  /** Tham so dau vao cong khai cua workflow (de goi y placeholder {{input.x}}). */
  workflowParams: ParamField[];
  target: NodeTargetInfo | null;
  /** Tra schema/ten cua 1 node model/nhom bat ky (de liet ke THAM SO node truoc). */
  resolveTarget: (node: WorkflowNode) => NodeTargetInfo | null;
  /** Danh sach model & nhom model de doi thuc the ma node tro toi. */
  modelOptions: Array<{ value: string; label: string }>;
  groupOptions: Array<{ value: string; label: string }>;
}

type Choice = { value: string; label: string };
type ChoiceGroup = { label: string; options: Choice[] };

/**
 * Cac lua chon "Lay tu…", CHIA NHOM cho de chon:
 *  1. Input cua workflow ({{input.x}})
 *  2. Tham so cac node truoc ({{nodes.<id>.input.<field>}} — gia tri THAT da gui cho buoc do)
 *  3. Ket qua cac node truoc ({{nodes.<id>.output_*}})
 */
function buildChoiceGroups(
  graph: WorkflowGraph,
  selfId: string,
  workflowParams: ParamField[],
  resolveTarget: (node: WorkflowNode) => NodeTargetInfo | null,
): ChoiceGroup[] {
  const wfInputs: Choice[] = workflowParams.map((f) => ({
    value: `{{input.${f.name}}}`,
    label: `${f.label || f.name} (${f.name})`,
  }));

  const nodeInputs: Choice[] = [];
  const nodeOutputs: Choice[] = [];
  for (const n of graph.nodes) {
    if (n.id === selfId || n.type === 'start' || n.type === 'end') continue;
    const label = n.name || n.id;
    if (n.type === 'text' || n.type === 'condition') {
      nodeOutputs.push({ value: `{{nodes.${n.id}.output_text}}`, label: `${label} → văn bản` });
    } else if (n.type === 'image' || n.type === 'video') {
      nodeOutputs.push({ value: `{{nodes.${n.id}.output_url}}`, label: `${label} → URL` });
    } else {
      // Node model/nhom: lo ra ca THAM SO DAU VAO cua no (vd aspect_ratio) — node Dieu kien
      // phia sau thuong xet chinh gia tri nay.
      const schema = resolveTarget(n)?.schema ?? [];
      const labelOf = new Map(schema.map((f) => [f.name, f.label || f.name]));
      for (const key of Object.keys(n.params ?? {})) {
        if (key === '__extra') continue;
        nodeInputs.push({
          value: `{{nodes.${n.id}.input.${key}}}`,
          label: `${label} → ${labelOf.get(key) || key} (${key})`,
        });
      }
      nodeOutputs.push({ value: `{{nodes.${n.id}.output_text}}`, label: `${label} → văn bản` });
      nodeOutputs.push({ value: `{{nodes.${n.id}.output_url}}`, label: `${label} → URL` });
      nodeOutputs.push({ value: `{{nodes.${n.id}.output_urls}}`, label: `${label} → nhiều URL` });
    }
  }

  return [
    { label: 'Input của workflow', options: wfInputs },
    { label: 'Tham số các node trước (input)', options: nodeInputs },
    { label: 'Kết quả các node trước (output)', options: nodeOutputs },
  ].filter((g) => g.options.length > 0);
}

/** Select chon placeholder — dropdown rong theo noi dung, KHONG cat chu. */
const CHOICE_SELECT_PROPS = {
  showSearch: true,
  optionFilterProp: 'label',
  popupMatchSelectWidth: false as const,
  dropdownStyle: { maxWidth: 560 },
};

export default function NodeConfigPanel({
  graph,
  node,
  onChange,
  onDelete,
  onDuplicate,
  onExposeParams,
  workflowParams,
  target,
  resolveTarget,
  modelOptions,
  groupOptions,
}: Props) {
  const choiceGroups = useMemo(
    () => buildChoiceGroups(graph, node.id, workflowParams, resolveTarget),
    [workflowParams, graph, node.id, resolveTarget],
  );
  // Danh sach phang — cho cac nut "Chèn nhanh" cua node chuc nang.
  const inputChoices = useMemo(() => choiceGroups.flatMap((g) => g.options), [choiceGroups]);

  const setParam = (name: string, value: unknown) => {
    const params = { ...(node.params ?? {}) };
    if (value === undefined || value === '' || value === null) delete params[name];
    else params[name] = value;
    onChange({ params });
  };

  const isJobNode = node.type === 'model' || node.type === 'group';
  const ports = target ? inputPortsOfSchema(target.schema) : [];

  const renderParam = (f: ParamField) => {
    const raw = node.params?.[f.name];
    const cur = raw === undefined ? '' : typeof raw === 'string' ? raw : JSON.stringify(raw);
    const isPlaceholder = typeof cur === 'string' && cur.includes('{{');
    const kind = ports.find((p) => p.name === f.name)?.kind ?? 'text';

    return (
      <div key={f.name} style={{ marginBottom: 12 }}>
        <div style={{ marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 8, height: 8, borderRadius: 8, background: PORT_COLOR[kind], display: 'inline-block' }} />
          <b>{f.label || f.name}</b>
          {f.required && <span style={{ color: 'red' }}>*</span>}
          <Tag style={{ marginInlineStart: 'auto' }}>{f.name}</Tag>
        </div>
        <Space.Compact style={{ width: '100%' }}>
          <Select
            {...CHOICE_SELECT_PROPS}
            style={{ width: 190 }}
            placeholder="Lấy từ…"
            allowClear
            value={isPlaceholder ? cur : undefined}
            options={choiceGroups}
            onChange={(v) => setParam(f.name, v ?? '')}
          />
          {f.type === 'select' && !isPlaceholder ? (
            <Select
              style={{ flex: 1 }}
              allowClear
              placeholder="hoặc chọn giá trị cố định"
              value={(cur as string) || undefined}
              options={(f.options || []).map((o) => ({ value: String(o), label: String(o) }))}
              onChange={(v) => setParam(f.name, v ?? '')}
            />
          ) : f.type === 'bool' && !isPlaceholder ? (
            <span style={{ padding: '4px 10px' }}>
              <Switch checked={raw === true} onChange={(v) => setParam(f.name, v)} />
            </span>
          ) : f.type === 'number' && !isPlaceholder ? (
            <InputNumber
              style={{ flex: 1 }}
              value={typeof raw === 'number' ? raw : undefined}
              onChange={(v) => setParam(f.name, v ?? '')}
            />
          ) : (
            <Input
              style={{ flex: 1 }}
              placeholder={
                UPLOAD_TYPES.includes(f.type) ? 'hoặc dán URL công khai' : 'hoặc nhập giá trị cố định / chèn {{…}}'
              }
              value={cur as string}
              onChange={(e) => setParam(f.name, e.target.value)}
            />
          )}
        </Space.Compact>
      </div>
    );
  };

  return (
    <div style={{ padding: 4 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
        <Tag color="blue">{NODE_TYPE_LABEL[node.type]}</Tag>
        {node.type !== 'start' && node.type !== 'end' && (
          <Space size={8}>
            <Button size="small" icon={<CopyOutlined />} onClick={onDuplicate}>
              Nhân bản
            </Button>
            <Button danger size="small" icon={<DeleteOutlined />} onClick={onDelete}>
              Xóa node
            </Button>
          </Space>
        )}
      </Space>

      <div style={{ marginTop: 12 }}>
        <div style={{ marginBottom: 4 }}>
          <b>Tên hiển thị</b>
        </div>
        <Input value={node.name ?? ''} onChange={(e) => onChange({ name: e.target.value })} />
      </div>

      {isJobNode && (
        <>
          <Divider orientation="left" plain style={{ marginTop: 16 }}>
            {node.type === 'model' ? 'Model' : 'Nhóm model'}
          </Divider>
          <Select
            showSearch
            optionFilterProp="label"
            style={{ width: '100%' }}
            placeholder={node.type === 'model' ? 'Chọn model' : 'Chọn nhóm model'}
            value={node.ref}
            options={node.type === 'model' ? modelOptions : groupOptions}
            onChange={(v) => onChange({ ref: v })}
          />
          {!target && node.ref && (
            <Alert style={{ marginTop: 8 }} type="error" showIcon message="Không tìm thấy — có thể đã bị xóa. Hãy chọn lại." />
          )}

          <Divider orientation="left" plain style={{ marginTop: 16 }}>
            Tham số đầu vào
          </Divider>
          {target && target.schema.length > 0 && (
            <Alert
              style={{ marginBottom: 12 }}
              type="info"
              showIcon
              message={
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  <span>
                    Muốn phần mềm thứ 3 gửi tham số vào node này <b>y hệt gọi thẳng model/nhóm</b>?
                  </span>
                  <Button size="small" type="primary" ghost onClick={onExposeParams}>
                    Lấy tham số từ input công khai (API)
                  </Button>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    Tự khai báo các tham số còn thiếu vào tab "Thông tin &amp; tham số" (giá trị cố định
                    hiện tại thành mặc định) và trỏ tham số node về {'{{input.<tên>}}'}. Tham số đang lấy
                    từ node khác được giữ nguyên.
                  </Typography.Text>
                </Space>
              }
            />
          )}
          {!target && <Typography.Text type="secondary">Chọn model/nhóm để hiện tham số.</Typography.Text>}
          {target && !target.schema.length && (
            <Typography.Text type="warning">
              Model/nhóm này chưa khai báo tham số — dùng "Tham số thô (JSON)" bên dưới.
            </Typography.Text>
          )}
          {target?.schema.filter((f) => f.enabled !== false).map(renderParam)}

          <Divider orientation="left" plain>
            Tham số thô (JSON) — gộp thêm
          </Divider>
          <Input.TextArea
            rows={3}
            style={{ fontFamily: 'monospace' }}
            placeholder='{ "seed": 123 }'
            value={typeof node.params?.__extra === 'string' ? (node.params.__extra as string) : ''}
            onChange={(e) => setParam('__extra', e.target.value)}
          />
          <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 4 }}>
            Bỏ trống nếu không cần. Nội dung được gộp vào input của bước này khi chạy.
          </Typography.Paragraph>
        </>
      )}

      {(node.type === 'text' || node.type === 'image' || node.type === 'video') && (
        <>
          <Divider orientation="left" plain style={{ marginTop: 16 }}>
            {node.type === 'text' ? 'Nội dung văn bản' : node.type === 'image' ? 'URL ảnh' : 'URL video'}
          </Divider>
          <Input.TextArea
            rows={4}
            value={node.template ?? ''}
            onChange={(e) => onChange({ template: e.target.value })}
            placeholder={
              node.type === 'text'
                ? 'Ví dụ: Hãy vẽ minh họa cho: {{input.prompt}}'
                : 'https://… hoặc {{input.input_image}}'
            }
          />
          <Space wrap style={{ marginTop: 8 }}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Chèn nhanh:
            </Typography.Text>
            {inputChoices.slice(0, 8).map((c) => (
              <Button
                key={c.value}
                size="small"
                onClick={() => onChange({ template: `${node.template ?? ''}${c.value}` })}
              >
                {c.label}
              </Button>
            ))}
          </Space>
        </>
      )}

      {node.type === 'condition' && (
        <>
          <Divider orientation="left" plain style={{ marginTop: 16 }}>
            Giá trị đem so sánh
          </Divider>
          <Space.Compact style={{ width: '100%' }}>
            <Select
              {...CHOICE_SELECT_PROPS}
              style={{ width: 190 }}
              placeholder="Lấy từ…"
              allowClear
              value={(node.condInput ?? '').includes('{{') ? node.condInput : undefined}
              options={choiceGroups}
              onChange={(v) => onChange({ condInput: v ?? '' })}
            />
            <Input
              style={{ flex: 1 }}
              placeholder="hoặc nhập chuỗi có {{…}}"
              value={node.condInput ?? ''}
              onChange={(e) => onChange({ condInput: e.target.value })}
            />
          </Space.Compact>

          <Divider orientation="left" plain style={{ marginTop: 16 }}>
            Các trường hợp (xét từ trên xuống — khớp đầu tiên thắng)
          </Divider>
          {(node.cases ?? []).map((c, i) => (
            <Space.Compact key={c.id} style={{ width: '100%', marginBottom: 8 }}>
              <span
                style={{
                  width: 14,
                  alignSelf: 'center',
                  textAlign: 'center',
                  color: CASE_COLORS[i % CASE_COLORS.length],
                  fontSize: 16,
                  lineHeight: '32px',
                }}
              >
                ●
              </span>
              <Select
                style={{ width: 160 }}
                value={c.operator}
                options={Object.entries(CONDITION_OPERATOR_LABEL).map(([value, label]) => ({ value, label }))}
                onChange={(op) =>
                  onChange({
                    cases: (node.cases ?? []).map((x) =>
                      x.id === c.id ? { ...x, operator: op as WorkflowConditionOperator } : x,
                    ),
                  })
                }
              />
              <Input
                style={{ flex: 1 }}
                placeholder={operatorNeedsValue(c.operator) ? 'giá trị (vd 9:16)' : '(không cần giá trị)'}
                disabled={!operatorNeedsValue(c.operator)}
                value={c.value ?? ''}
                onChange={(e) =>
                  onChange({
                    cases: (node.cases ?? []).map((x) => (x.id === c.id ? { ...x, value: e.target.value } : x)),
                  })
                }
              />
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={() => onChange({ cases: (node.cases ?? []).filter((x) => x.id !== c.id) })}
              />
            </Space.Compact>
          ))}
          <Button
            icon={<PlusOutlined />}
            size="small"
            onClick={() =>
              onChange({
                cases: [
                  ...(node.cases ?? []),
                  { id: `c${Date.now().toString(36)}`, operator: 'equals', value: '' } as WorkflowConditionCase,
                ],
              })
            }
          >
            Thêm trường hợp
          </Button>
          <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 10 }}>
            Mỗi trường hợp là một chấm tròn (cổng ra) trên node — kéo dây sang node muốn chạy khi khớp.
            Không khớp trường hợp nào thì đi theo cổng <b>"còn lại"</b>; nếu không nối "còn lại", workflow
            báo lỗi (hoặc chạy lại theo "Số lần chạy lại khi lỗi").
          </Typography.Paragraph>
        </>
      )}

      {node.type === 'start' && (
        <Alert
          style={{ marginTop: 16 }}
          type="info"
          showIcon
          message="Node Bắt đầu nhận input của workflow"
          description="Khai báo các tham số đầu vào ở tab Thông tin → Tham số đầu vào. Dùng {{input.<tên>}} trong các node phía sau."
        />
      )}
      {node.type === 'end' && (
        <Alert
          style={{ marginTop: 16 }}
          type="info"
          showIcon
          message="Node Kết thúc trả kết quả"
          description="Kết quả của node nối vào đây chính là output trả cho phần mềm thứ 3 (output_url / output_text)."
        />
      )}
    </div>
  );
}
